import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@shared/lib/cn';

const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-150 ease-out ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-0 ' +
  'disabled:pointer-events-none disabled:opacity-40 select-none whitespace-nowrap',
  {
    variants: {
      variant: {
        primary:
          'bg-accent-600 text-white shadow-card hover:bg-accent-700 active:translate-y-[1px] hover:shadow-glow-accent',
        secondary:
          'bg-bg-2 text-fg-hi border border-line-subtle/10 hover:bg-bg-3 active:translate-y-[1px]',
        ghost:
          'bg-transparent text-fg-md hover:bg-bg-2 hover:text-fg-hi',
        outline:
          'bg-transparent text-fg-hi border border-line-strong/20 hover:bg-bg-2 hover:border-line-strong/40',
        danger:
          'bg-danger/90 text-white hover:bg-danger active:translate-y-[1px]',
        link:
          'bg-transparent text-accent-300 hover:text-accent-500 underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button> & { asChild?: boolean };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, ...props },
  ref
) {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} className={cn(button({ variant, size }), className)} {...props} />;
});
